import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Trash2, Pencil, ChevronUp, X, Search, FolderInput } from 'lucide-react';
import { IS_SAAS } from '../config/edition';
import { historyService } from '../services/historyService';
import { accountLabel, accountInitials } from '../utils/accountName';
import { metricsService } from '../services/metricsService';
import { logUiEvent } from '../services/uiLogger';
import { splitByHighlights } from '../utils/searchHighlight';
import { RECOGNITION_V2 } from './recognition/RecognitionScreen';
import { FEEDBACK_ENTRY_LABEL } from '../lib/feedbackEntry';
import { isAlarming, quotaEnforced, quotaLeftMinutes, quotaLevel, quotaLevelByBalance } from '../lib/quota';
import { formatRub } from '../lib/billing';
import { CLIENT_URL } from '../lib/host';
import HistoryFolders from './history/HistoryFolders';
import MoveToFolderMenu from './history/MoveToFolderMenu';
import { hintVisible, dismissHint, markShown } from '../lib/featureHint';

// Логотип «первый.Бит» — точка-квадрат розовая (типографика, по брендбуку)
function BitLogo() {
  return (
    <span className="inline-flex items-baseline font-display font-extrabold text-[17px] tracking-[-0.025em] text-paper-ink">
      <span className="font-normal">первый</span>
      <span className="inline-block w-[6px] h-[6px] mx-[2px] bg-paper-accent" />
      <span>Бит</span>
    </span>
  );
}

// Марка продукта «Бит.Ньютон» (Ньютон розовым). Одно написание везде — решение
// после дизайн-спринта 23.09: было четыре, латиница и кириллица вперемешку.
function ProductMark() {
  return (
    <span className="font-display font-extrabold text-[13px] tracking-[-0.01em] text-paper-ink">
      Бит.<span className="text-paper-accent">Ньютон</span>
    </span>
  );
}

// Иконок у пунктов нет: в «Бумаге» навигация набрана только словами.
export const NAV = [
  { id: 'transcription', label: 'Транскрибация' },
  { id: 'protocol', label: 'Протокол' },
  { id: 'dual', label: 'АТС (стерео)' },
  { id: 'tts', label: 'Озвучка' },
  { id: 'streaming', label: 'Потоковое' },
  // «Метрики» (личные, с 08.09.2026) — в меню пользователя, рядом с личным
  // кабинетом: это «про меня», а не рабочий инструмент, и строка навигации
  // нужнее списку записей (решение владельца 10.10.2026).
  // Организация — тем, кто в ней состоит (плательщику и участникам), любой роли:
  // приглашённый сотрудник остаётся ролью user. В издании onprem организаций нет —
  // они живут в bit-billing. До 24.09 была блоком в личном кабинете (Р6).
  { id: 'organization', label: 'Организация', needsOrganization: true, saasOnly: true },
  // «Тарифы» пунктом меню больше нет — они в личном кабинете, кнопкой «Сменить
  // тариф» (дизайн-спринт 23.09, 3.4). Экран тот же, права те же: canOpenPricing.
];

/**
 * Доступны ли «Тарифы»: клиенту и админу, только в издании saas — в onprem
 * платёжного контура нет, а тарифы это наша SaaS-модель (см. config/edition.js).
 */
export function canOpenPricing(role) {
  return IS_SAAS && ['client', 'admin'].includes(role);
}

// Единый экран съедает «Потоковое» и «АТС»: это те же способы получить
// расшифровку, и человеку они видны как выбор источника, а не как три
// отдельных продукта.
const MERGED_INTO_RECOGNITION = ['streaming', 'dual'];

/**
 * Пункты меню для конкретного человека. Вынесено из разметки, чтобы права
 * и фича-флаг проверялись тестом, а не прочтением JSX.
 */
/**
 * Куда ведёт логотип: клиенту — витрина клиентского домена, сотруднику — демо
 * для продаж (решение владельца 03.10, обращения 547d613c и d087321d; до этого
 * с 28.09 вело на предложение). В демо «Как продать», тарифы и кнопка «Страница
 * для клиента» — до предложения оттуда один клик. С 04.11.2026 /demo_latest —
 * переход на итоги месяца в Мастерской (public/demo_latest/index.html): адрес
 * страницы — наш домен, в коде бандла его быть не должно (no-hardcoded-hosts).
 */
export function logoHref(role) {
  if (role === 'client' && CLIENT_URL) return CLIENT_URL;
  return '/demo_latest/index.html';
}

export function visibleNav({ isAdmin, role, recognitionV2, hasOrganization = false }) {
  return NAV
    .filter((it) => !it.adminOnly || isAdmin)
    .filter((it) => !it.roles || it.roles.includes(role))
    .filter((it) => !it.needsOrganization || hasOrganization)
    // Издание onprem не несёт коммерции: платёжного контура у клиента нет, тарифы —
    // наша SaaS-модель. Пункт скрывается независимо от роли, в том числе у админа.
    .filter((it) => !it.saasOnly || IS_SAAS)
    .filter((it) => !recognitionV2 || !MERGED_INTO_RECOGNITION.includes(it.id))
    .map((it) => (recognitionV2 && it.id === 'transcription'
      ? { ...it, label: 'Распознавание' }
      : it));
}

// Метаподпись записи: «38 мин · сегодня»
function formatMeta(item) {
  const parts = [];
  const dur = item.meta && item.meta.duration;
  if (dur) {
    const min = Math.max(1, Math.round(dur / 60));
    parts.push(`${min} мин`);
  }
  if (item.created_at) {
    const d = new Date(item.created_at);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const yest = new Date(now); yest.setDate(now.getDate() - 1);
    if (sameDay) parts.push('сегодня');
    else if (d.toDateString() === yest.toDateString()) parts.push('вчера');
    else parts.push(d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }));
  }
  return parts.join(' · ');
}

// Размер страницы Истории. Бэкенд принимает limit до 200 (bit-history/app/api.py:91),
// но грузим по 50: боковая панель узкая, а первый экран должен открываться быстро.
const HISTORY_PAGE = 50;

// Как часто видимая вкладка перечитывает список. Минута — компромисс: запись
// после долгой обработки появляется сама, а лишних запросов от 180 активных
// пользователей в день это добавляет немного.
const HISTORY_POLL_MS = 60000;

// Поиск уходит на сервер, когда человек перестал печатать на столько.
const SEARCH_DEBOUNCE_MS = 300;

// Сколько висит полоса «Запись удалена · Отменить». Настоящий DELETE уходит,
// только когда время вышло (владелец, 10.10: удаление было необратимым и без вопроса).
export const UNDO_DELETE_MS = 7000;

// Ширина панели на десктопе: тянется за правый край, помнится в браузере
// (решение владельца 29.09). Потолок — треть окна, но не меньше 400px: на
// широком мониторе Историю хотят видеть шире (владелец, 29.09). Шаг клавишами — 16px.
export const SIDEBAR_WIDTH_DEFAULT = 248;
export const SIDEBAR_WIDTH_MIN = 200;
export const SIDEBAR_WIDTH_MAX = 400;
export const SIDEBAR_WIDTH_KEY = 'newton.sidebarWidth';
const SIDEBAR_WIDTH_STEP = 16;
// Предел для сохранённого значения: сколько бы ни показывал монитор, шире не храним.
const SIDEBAR_WIDTH_HARD_CAP = 1600;

export const sidebarMaxWidth = () =>
  Math.max(SIDEBAR_WIDTH_MAX, Math.floor((window.innerWidth || 0) / 3));

const clampWidth = (w, max = sidebarMaxWidth()) =>
  Math.min(max, Math.max(SIDEBAR_WIDTH_MIN, Math.round(w)));

// Хранилище может бросить (приватное окно, запрет сайта) или быть пустым —
// тогда просто ширина по умолчанию.
function readSavedWidth() {
  try {
    const n = Number(window.localStorage.getItem(SIDEBAR_WIDTH_KEY));
    // Сохранённое не режем по текущему окну: сузили окно и вернули — ширина та же.
    return Number.isFinite(n) && n > 0 ? clampWidth(n, SIDEBAR_WIDTH_HARD_CAP) : SIDEBAR_WIDTH_DEFAULT;
  } catch (e) {
    return SIDEBAR_WIDTH_DEFAULT;
  }
}

function saveWidth(w) {
  try { window.localStorage.setItem(SIDEBAR_WIDTH_KEY, String(w)); } catch (e) { /* приватное окно */ }
}

// Подпись кнопки меню и инициалы — общее правило «задано ли имя»
// (utils/accountName.js), то же, что у подписи бота на встрече.
export { accountLabel, accountInitials } from '../utils/accountName';

// Где нашлось — пометка в строке выдачи.
const MATCH_FIELD_LABEL = { title: 'в названии', summary: 'в протоколе', transcript: 'в расшифровке' };

// Найденное — элементами React, а не innerHTML: текст записи приходит от
// пользователя и разметкой быть не должен.
function Highlighted({ text, highlights }) {
  return splitByHighlights(text || '', highlights).map((part, i) => (part.hit
    ? <mark key={i} className="bg-paper-accent/15 text-paper-ink rounded-[2px]">{part.text}</mark>
    : <React.Fragment key={i}>{part.text}</React.Fragment>));
}

export default function AppSidebar({
  activeTab, onTabChange, isAdmin, role, email, displayName, onOpenDemo,
  onOpenProfile, onOpenAdmin, onLogout, onOpenFeedback, unreadFeedback = 0,
  onOpenHistoryItem, refreshKey,
  historyEnabled, onEnableHistory, hasOrganization = false,
  mobile = false, drawerOpen = true, onQuotaChange, onWidthChange,
  onFoldersChange, openFolderRequest, onItemMoved,
  onItemDeleteStart, onItemDeleteUndo, onItemDeleted,
}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [quotaText, setQuotaText] = useState('');
  const [quota, setQuota] = useState(null);
  // Уровень остатка: пока строка была ровного серого цвета, заметить её было нельзя.
  const [quotaLevelValue, setQuotaLevelValue] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  // Поиск по Истории. Поле стоит всегда; results === null — показываем обычный
  // список, массив — выдачу вместо него. resultsQuery — запрос, к которому
  // относится выдача: пока человек допечатывает, на экране ещё прежняя, и
  // открывать запись надо с тем запросом, по которому она нашлась.
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);
  const [results, setResults] = useState(null);
  const [resultsTotal, setResultsTotal] = useState(0);
  const [resultsQuery, setResultsQuery] = useState('');
  const [searchState, setSearchState] = useState('idle'); // idle|loading|done|error|unavailable
  // Номер последнего запроса: ответ более раннего, пришедший позже, отбрасывается.
  const reqSeq = useRef(0);
  // Сколько записей обычного поиска уже загружено: «Показать ещё» листает его, а в
  // выдаче сверху стоят ещё и находки по смыслу.
  const lexLoaded = useRef(0);

  // Папки Истории. folders === null — функции нет (сервер отвечает 404 не всем):
  // тогда панель выглядит ровно как до папок. openFolderId — открытая папка
  // (null — все записи), moveMenuFor — id записи с открытым меню «В папку».
  const [folders, setFolders] = useState(null);
  // Для таймера удаления: его замыкание видит устаревшее значение.
  const foldersRef = useRef(null);
  foldersRef.current = folders;
  const [openFolderId, setOpenFolderId] = useState(null);
  const [moveMenuFor, setMoveMenuFor] = useState(null);
  const [folderError, setFolderError] = useState('');

  // Ширина панели (только десктоп). drag — точка начала перетаскивания,
  // widthRef — последнее значение для записи по отпусканию кнопки.
  // width — желаемая ширина, shownWidth — она же, урезанная по текущему окну.
  const [width, setWidth] = useState(readSavedWidth);
  const [maxWidth, setMaxWidth] = useState(sidebarMaxWidth);
  const shownWidth = Math.min(width, maxWidth);
  const [dragging, setDragging] = useState(false);
  const drag = useRef(null);
  const widthRef = useRef(shownWidth);
  widthRef.current = shownWidth;

  // Потолок — треть окна: пересчитываем, когда окно меняет размер.
  useEffect(() => {
    if (mobile) return undefined;
    const onResize = () => setMaxWidth(sidebarMaxWidth());
    // Сразу пересчитываем: окно могли развернуть, пока была шторка и слушателя не было.
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [mobile]);

  // Номер запроса списка: ответ, пришедший после более нового load (клик по папке,
  // опрос, фокус), показывать нельзя — он перекрыл бы список открытой папки.
  // Устаревший запрос не трогает и loading: им распоряжается новый.
  const loadSeq = useRef(0);

  // Смена папки: прежний список не должен висеть под новым заголовком, пока идёт
  // запрос. total тоже сбрасываем, иначе на пустом списке мигнёт «Показать ещё (N)».
  // Сброс стоит выше эффекта загрузки, чтобы выполниться раньше него.
  const shownFolder = useRef(openFolderId);
  useEffect(() => {
    if (shownFolder.current === openFolderId) return;
    shownFolder.current = openFolderId;
    setItems([]);
    setTotal(0);
    setLoading(true);
  }, [openFolderId]);

  const load = useCallback(async () => {
    loadSeq.current += 1;
    const seq = loadSeq.current;
    setLoading(true);
    setLoadError(false);
    try {
      const data = await historyService.list({
        limit: HISTORY_PAGE, ...(openFolderId ? { folder_id: openFolderId } : {}),
      });
      if (seq !== loadSeq.current) return;
      setItems(data.items || []);
      setTotal(typeof data.total === 'number' ? data.total : (data.items || []).length);
    } catch (e) {
      if (seq !== loadSeq.current) return;
      setLoadError(true);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [openFolderId]);

  // Список папок. Любой сбой (в том числе отсутствие метода или 404) — «папок нет».
  // Список записей не трогает: счётчики и подписи обновляются отдельно от него.
  const loadFolders = useCallback(async () => {
    try {
      const data = await historyService.listFolders();
      setFolders(data ? data.folders || [] : null);
    } catch (_) {
      // Сбой при перечитывании (сеть) уже известные папки не стирает: иначе путь
      // к записи в верхней строке мигал бы и пропадал. Первый показ — «папок нет»:
      // folders и так null.
    }
  }, []);

  // Догрузка следующей страницы. skip считаем по числу уже показанных, а не по
  // номеру страницы: между запросами могла появиться новая запись, и тогда
  // страницы сдвинутся. От сдвига спасает не арифметика, а склейка по id ниже.
  const loadMore = useCallback(async () => {
    const seq = loadSeq.current;
    setLoadingMore(true);
    try {
      const data = await historyService.list({
        limit: HISTORY_PAGE, ...(openFolderId ? { folder_id: openFolderId } : {}), skip: items.length,
      });
      // Пока шла догрузка, список перечитали или сменили папку: страница чужая.
      if (seq !== loadSeq.current) return;
      const fresh = data.items || [];
      setItems((prev) => {
        const seen = new Set(prev.map((it) => it.id));
        return prev.concat(fresh.filter((it) => !seen.has(it.id)));
      });
      if (typeof data.total === 'number') setTotal(data.total);
    } catch (e) {
      // Молча: первая страница уже показана, обрывать её сообщением об ошибке
      // хуже, чем оставить кнопку — человек нажмёт ещё раз.
    } finally {
      setLoadingMore(false);
    }
  }, [items.length, openFolderId]);

  useEffect(() => { if (historyEnabled) load(); }, [historyEnabled, refreshKey, load]);
  // Папки перечитываем и по refreshKey вместе со списком: счётчики и подписи
  // не должны отставать от переноса, сделанного на экране записи.
  useEffect(() => { if (historyEnabled) loadFolders(); }, [historyEnabled, refreshKey, loadFolders]);

  // Приложению нужен список папок: путь к записи в верхней строке рисует оно.
  useEffect(() => { if (onFoldersChange) onFoldersChange(folders); }, [folders, onFoldersChange]);

  // Запрос уходит, когда человек перестал печатать. Текст запроса в телеметрию
  // не пишем — только длину и сколько нашлось.
  //
  // Номер запроса растёт на каждое изменение поля, а не в момент отправки:
  // иначе ответ прежнего запроса, пришедший в окне ожидания нового, совпадал
  // по номеру и на миг показывал выдачу по уже стёртому тексту (ревью 29.09).
  // Зависим от текста без краевых пробелов: пробел в конце запроса не меняет и
  // не должен ронять летящий ответ ради того же самого запроса.
  const q = query.trim();
  useEffect(() => {
    reqSeq.current += 1;
    const seq = reqSeq.current;
    if (!q) {
      setResults(null);
      setSearchState('idle');
      return undefined;
    }
    // Одно поле — два поиска сразу (решение владельца 02.11: «никаких переключателей»).
    // Сверху — по смыслу: запись, место в разговоре и цитата; ниже — совпадения
    // обычного поиска, которых там не оказалось (фраза в кавычках, начало фамилии).
    // Поиск по смыслу не ответил — выдача обычного поиска, без сообщения об ошибке.
    const timer = setTimeout(async () => {
      setSearchState('loading');
      const [lex, sense] = await Promise.allSettled([
        historyService.list({ limit: HISTORY_PAGE, q }),
        (async () => (q.length >= 2 ? historyService.semantic(q) : []))(),
      ]);
      if (seq !== reqSeq.current) return;
      const senseItems = (sense.status === 'fulfilled' ? sense.value || [] : []).map((it) => ({ ...it, sense: true }));
      if (lex.status === 'rejected' && !senseItems.length) {
        lexLoaded.current = 0;
        setResults([]);
        setResultsTotal(0);
        setResultsQuery(q);
        setSearchState(lex.reason?.response?.status === 503 ? 'unavailable' : 'error');
        return;
      }
      const data = lex.status === 'fulfilled' ? lex.value || {} : {};
      const lexItems = data.items || [];
      const lexTotal = typeof data.total === 'number' ? data.total : lexItems.length;
      const seen = new Set(senseItems.map((it) => it.id));
      const rest = lexItems.filter((it) => !seen.has(it.id));
      lexLoaded.current = lexItems.length;
      setResults(senseItems.concat(rest));
      setResultsTotal(senseItems.length + lexTotal - (lexItems.length - rest.length));
      setResultsQuery(q);
      setSearchState('done');
      logUiEvent('history_search', {
        component: 'history',
        meta: { q_len: q.length, results: lexTotal, sense: senseItems.length, sense_ok: sense.status === 'fulfilled' },
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [q]);

  // «Показать ещё» в выдаче — та же склейка по id, что у loadMore списка.
  const loadMoreResults = async () => {
    const seq = reqSeq.current;
    setLoadingMore(true);
    try {
      const data = await historyService.list({ limit: HISTORY_PAGE, skip: lexLoaded.current, q: resultsQuery });
      if (seq !== reqSeq.current) return;
      const fresh = data.items || [];
      lexLoaded.current += fresh.length;
      const seen = new Set(results.map((it) => it.id));
      const added = fresh.filter((it) => !seen.has(it.id));
      setResults((prev) => prev.concat(added));
      // Совпавшие с находками по смыслу уже на экране — из остатка они выпадают;
      // пустая страница значит, что листать больше нечего.
      setResultsTotal((t) => (fresh.length ? t - (fresh.length - added.length) : results.length + added.length));
    } catch (e) {
      // Молча, как у списка: первая страница выдачи уже на экране.
    } finally {
      setLoadingMore(false);
    }
  };

  // Очистить поиск — вернуть обычный список. Перечитываем его, только если на
  // экране была выдача: пока человек искал, в Истории могла появиться запись.
  // Стёртые пробелы или текст, не доживший до запроса, списка не скрывали.
  const clearSearch = () => {
    const hadResults = results !== null;
    reqSeq.current += 1;
    setQuery('');
    setResults(null);
    setSearchState('idle');
    if (hadResults) load();
  };

  // 🔴 Список сам себя догоняет. До 08.09.2026 он грузился ровно дважды: при
  // открытии страницы и после сохранения записи ЭТОЙ вкладкой. Человек, который
  // грузит файлы в нескольких вкладках (а так и делают, когда файлов четыре),
  // видел в каждой из них список на момент её открытия — и решал, что
  // расшифровок нет вовсе. Обращение 49823adf: четыре записи лежали в Истории
  // полтора часа, пока автор писал, что их нет.
  //
  // Опрашиваем только видимую вкладку: фоновая ничего не показывает, а запросы
  // от неё идут в общий счёт.
  useEffect(() => {
    if (!historyEnabled) return undefined;
    const refresh = () => { if (document.visibilityState === 'visible') load(); };
    const timer = setInterval(refresh, HISTORY_POLL_MS);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [historyEnabled, load]);

  // Квота «осталось N мин» — best-effort, не критично. Перечитывается и по
  // refreshKey: он растёт, когда работа легла в Историю, то есть минуты уже
  // списаны. Раньше остаток обновлялся только перезагрузкой страницы (прогон 24.09).
  useEffect(() => {
    let alive = true;
    if (!email) return;
    metricsService.getQuota(email)
      .then((q) => {
        if (!alive || !q) return;
        setQuota(q);
        const left = quotaLeftMinutes(q);
        if (left !== null) setQuotaText(`осталось ${left} мин`);
        // У «Оплаты по факту» лимит минут — это подарок, а used за месяц почти
        // всегда его превышает: уровень по проценту от лимита красил бы строку
        // в красный оплатившему клиенту с деньгами на счету. Считаем по деньгам.
        const hasBalance = q.balance_rub !== undefined && q.balance_rub !== null;
        // Тревожный цвет — только тем, кого лимит действительно останавливает.
        setQuotaLevelValue(
          hasBalance ? quotaLevelByBalance(q) : (quotaEnforced(q) ? quotaLevel(q) : null),
        );
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [email, refreshKey]);

  // Ширину узнаёт и App: плашка «Идёт обработка» стоит правее панели.
  // Сообщаем по отпусканию, а не на каждом движении — иначе перерисовка всего
  // приложения на каждый пиксель.
  useEffect(() => {
    if (!mobile && !dragging && onWidthChange) onWidthChange(shownWidth);
  }, [mobile, dragging, shownWidth, onWidthChange]);

  // Пока тянем, текст страницы не выделяется. Возвращаем и при размонтировании
  // посреди перетаскивания.
  useEffect(() => {
    if (!dragging) return undefined;
    const prev = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    return () => { document.body.style.userSelect = prev; };
  }, [dragging]);

  // Окно сузилось до телефона посреди перетаскивания: ручка размонтируется,
  // pointerup в неё уже не придёт — иначе dragging застрял бы, а с ним и
  // запрет выделения текста на всей странице. Дотянутое сохраняем.
  useEffect(() => {
    if (!mobile || !drag.current) return;
    drag.current = null;
    setDragging(false);
    saveWidth(widthRef.current);
  }, [mobile]);

  const commitWidth = (w) => {
    const next = clampWidth(w, maxWidth);
    // Упёрлись в потолок узкого окна — не затираем сохранённую бóльшую ширину.
    if (next === shownWidth) return;
    setWidth(next);
    saveWidth(next);
  };

  // Захват указателя: движения и отпускание приходят в ручку, даже когда курсор
  // ушёл с неё, — слушатели на document не нужны и снимать нечего.
  const onResizeDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* нет в jsdom */ }
    drag.current = { x: e.clientX, w: widthRef.current };
    setDragging(true);
  };

  const onResizeMove = (e) => {
    if (!drag.current) return;
    setWidth(clampWidth(drag.current.w + e.clientX - drag.current.x, maxWidth));
  };

  const onResizeEnd = (e) => {
    if (!drag.current) return;
    drag.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* уже отпущен */ }
    setDragging(false);
    saveWidth(widthRef.current);
  };

  const onResizeKey = (e) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); commitWidth(shownWidth - SIDEBAR_WIDTH_STEP); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); commitWidth(shownWidth + SIDEBAR_WIDTH_STEP); }
    else if (e.key === 'Home') { e.preventDefault(); commitWidth(SIDEBAR_WIDTH_MIN); }
    else if (e.key === 'End') { e.preventDefault(); commitWidth(maxWidth); }
  };

  // Закрытие меню по клику вне
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  // --- Удаление с отменой ---
  // hidden — id записей, которые уже убраны с экрана, но ещё не удалены на
  // сервере (окно отмены или DELETE в полёте). undo — запись, чья полоса сейчас
  // на экране; undoRef/undoTimer — то же для таймера. Обратные вызовы приложению
  // берём из ref: таймер живёт дольше рендера, в котором он заведён.
  const [hidden, setHidden] = useState([]);
  const [undo, setUndo] = useState(null);
  const undoRef = useRef(null);
  const undoTimer = useRef(null);
  const mounted = useRef(true);
  const deleteCb = useRef({});
  deleteCb.current = { onItemDeleteStart, onItemDeleteUndo, onItemDeleted };

  // Закрыли панель посреди окна отмены: таймер снимаем, DELETE не шлём — запись
  // остаётся (ошибаемся в безопасную сторону).
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(undoTimer.current);
    };
  }, []);

  const unhide = (id) => setHidden((h) => h.filter((x) => x !== id));

  // Довести удаление до сервера. Запись остаётся скрытой до ответа: опрос списка
  // в этот момент не должен её вернуть.
  const finalizeDelete = async (id) => {
    if (undoRef.current === id) {
      clearTimeout(undoTimer.current);
      undoRef.current = null;
      setUndo(null);
    }
    try {
      await historyService.remove(id);
    } catch (_) {
      if (!mounted.current) return;
      unhide(id);
      setFolderError('Не удалось удалить запись');
      return;
    }
    if (mounted.current) {
      setItems((prev) => prev.filter((it) => it.id !== id));
      setTotal((n) => Math.max(0, n - 1));
      unhide(id);
      // Счётчики папок устарели; у учёток без папок лишнего запроса нет.
      if (foldersRef.current !== null) loadFolders();
    }
    if (deleteCb.current.onItemDeleted) deleteCb.current.onItemDeleted(id);
  };

  const handleDelete = (e, id, title) => {
    e.stopPropagation();
    // На телефоне корзина видна всегда и стоит под пальцем рядом с записью —
    // без вопроса случайное касание стирало бы расшифровку. Вопрос остаётся и
    // перед окном отмены.
    if (mobile && !window.confirm(`Удалить запись «${title || 'Без названия'}»?`)) return;
    // Второе удаление в окне отмены сразу доводит первое.
    if (undoRef.current && undoRef.current !== id) finalizeDelete(undoRef.current);
    setFolderError('');
    setHidden((h) => (h.includes(id) ? h : [...h, id]));
    undoRef.current = id;
    setUndo(id);
    clearTimeout(undoTimer.current);
    undoTimer.current = setTimeout(() => finalizeDelete(id), UNDO_DELETE_MS);
    if (deleteCb.current.onItemDeleteStart) deleteCb.current.onItemDeleteStart(id);
  };

  const undoDelete = () => {
    const id = undoRef.current;
    if (!id) return;
    clearTimeout(undoTimer.current);
    undoRef.current = null;
    setUndo(null);
    unhide(id);
    if (deleteCb.current.onItemDeleteUndo) deleteCb.current.onItemDeleteUndo(id);
  };

  const startRename = (e, item) => {
    e.stopPropagation();
    setEditingId(item.id);
    setEditValue(item.title || '');
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditValue('');
  };

  // Сохранение заголовка: пустое/неизменённое — просто выходим из правки.
  // Иначе оптимистично обновляем список, шлём PATCH, при сбое откатываем.
  const commitRename = (item) => {
    const next = editValue.trim();
    setEditingId(null);
    if (!next || next === item.title) return;
    const prevTitle = item.title;
    setItems((arr) => arr.map((it) => (it.id === item.id ? { ...it, title: next } : it)));
    historyService.patch(item.id, { title: next })
      .catch(() => {
        // best-effort откат
        setItems((arr) => arr.map((it) => (it.id === item.id ? { ...it, title: prevTitle } : it)));
      });
  };

  // --- Папки: только состояние и обработчики, разметка живёт в history/ ---
  const folderName = (id) => (folders || []).find((f) => f.id === id)?.name || null;
  const closeMoveMenu = useCallback(() => setMoveMenuFor(null), []);

  // Подсказка о папках (12.10.2026: за три дня ими воспользовался один человек).
  // Неделя — с первого открытия Ньютона этим человеком; первое чтение ниже и
  // начинает отсчёт. У кого папки уже есть, подсказка гасится насовсем — иначе
  // она появилась бы после удаления последней папки.
  const [foldersHint, setFoldersHint] = useState(() => hintVisible('folders'));
  // «Увидел» — только когда карточка действительно перед глазами: на телефоне
  // панель смонтирована и при закрытой шторке, а поверх списка может стоять поиск.
  const foldersHintOnScreen = foldersHint && folders !== null && folders.length === 0
    && openFolderId === null && results === null && (!mobile || drawerOpen);
  useEffect(() => {
    if (foldersHint && folders && folders.length > 0) {
      dismissHint('folders');
      setFoldersHint(false);
    } else if (foldersHintOnScreen && markShown('folders')) {
      logUiEvent('folders_hint', { component: 'history', meta: { action: 'shown' } });
    }
  }, [foldersHint, folders, foldersHintOnScreen]);

  const onFoldersHintAction = (action) => {
    dismissHint('folders');
    setFoldersHint(false);
    logUiEvent('folders_hint', { component: 'history', meta: { action } });
  };

  const openFolder = (id) => {
    setFolderError('');
    setMoveMenuFor(null);
    setOpenFolderId(id);
    // «← Все записи» (id === null) — возврат, а не открытие папки.
    if (id) logUiEvent('history_folder', { component: 'history', meta: { action: 'open' } });
  };

  // Ошибки create/rename уходят в поле самого блока (он их показывает), поэтому
  // здесь только пробрасываем.
  const createFolder = async (name) => {
    await historyService.createFolder(name);
    logUiEvent('history_folder', { component: 'history', meta: { action: 'create' } });
    await loadFolders();
  };

  const renameFolder = async (id, name) => {
    await historyService.renameFolder(id, name);
    logUiEvent('history_folder', { component: 'history', meta: { action: 'rename' } });
    await loadFolders();
  };

  // Блок не ловит ошибку удаления — сообщаем сами, иначе её не увидит никто.
  const deleteFolder = async (folder) => {
    setFolderError('');
    try {
      await historyService.deleteFolder(folder.id);
    } catch (_) {
      setFolderError('Не удалось удалить папку');
      return;
    }
    logUiEvent('history_folder', { component: 'history', meta: { action: 'delete' } });
    // Открытая папка: смена openFolderId сама перечитает список; иначе — подписи.
    if (openFolderId === folder.id) setOpenFolderId(null); else load();
    loadFolders();
  };

  // Запрос приложения открыть папку (id) или все записи (null) — «История» и
  // папка в пути к записи. Первый показ с уже выставленным seq запросом не считается.
  const handledOpenSeq = useRef(openFolderRequest ? openFolderRequest.seq : null);
  useEffect(() => {
    if (!openFolderRequest || openFolderRequest.seq === handledOpenSeq.current) return;
    handledOpenSeq.current = openFolderRequest.seq;
    // Поиск закрывает выдачу: иначе папка открылась бы под ней, и клик «ничего не делал» бы.
    reqSeq.current += 1;
    setQuery('');
    setResults(null);
    setSearchState('idle');
    setFolderError('');
    setMoveMenuFor(null);
    setOpenFolderId(openFolderRequest.id || null);
    if (openFolderRequest.id) logUiEvent('history_folder', { component: 'history', meta: { action: 'open' } });
  }, [openFolderRequest]);

  const moveItem = async (it, folderId) => {
    setMoveMenuFor(null);
    setFolderError('');
    try {
      await historyService.patch(it.id, { folder_id: folderId });
    } catch (_) {
      setFolderError('Не удалось переложить запись');
      return;
    }
    if (openFolderId && folderId !== openFolderId) {
      setItems((prev) => prev.filter((x) => x.id !== it.id));
      setTotal((n) => Math.max(0, n - 1));
    } else {
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, folder_id: folderId } : x)));
    }
    loadFolders();
    if (onItemMoved) onItemMoved(it.id, folderId);
    // Выбор той же папки (строка с ✓) — не перенос.
    if (folderId !== (it.folder_id || null)) {
      logUiEvent('history_folder', { component: 'history', meta: { action: 'move' } });
    }
  };

  const createFolderAndMove = async (it, name) => {
    const created = await historyService.createFolder(name);
    logUiEvent('history_folder', { component: 'history', meta: { action: 'create' } });
    await moveItem(it, created.id);
  };

  // Подпись папки в строке: только если папка известна (иначе запись «без папки»).
  const folderTag = (it) => {
    const name = it.folder_id ? folderName(it.folder_id) : null;
    return name ? (
      <span title={name} className="ml-1 inline-block max-w-[45%] truncate align-bottom rounded-badge bg-paper-bg px-1 text-[10px] text-paper-ink-2">
        {name}
      </span>
    ) : null;
  };

  // Скрытые (ждут удаления) из списков и выдачи убираем при показе, а не из
  // состояния: тогда «Отменить» возвращает строку на прежнее место, а
  // перезагрузка списка скрытую запись не воскрешает.
  const shownItems = hidden.length ? items.filter((it) => !hidden.includes(it.id)) : items;
  // Строка выдачи поиска: находка по смыслу или обычное совпадение.
  const renderResult = (it) => {
                  if (it.sense) {
                    // Находка по смыслу: место в разговоре или протокол и цитата.
                    // Клик открывает запись на этой минуте (или вкладку протокола).
                    const where = it.protocol ? 'в протоколе' : (it.at ? `в разговоре · ${it.at}` : 'в разговоре');
                    return (
                      <div
                        key={it.id}
                        data-testid="sense-hit"
                        onClick={() => onOpenHistoryItem(it.id, {
                          query: resultsQuery, field: it.protocol ? 'summary' : 'transcript', summaryId: null, at: it.at || null,
                        })}
                        className={`rounded-[10px] px-3 ${mobile ? 'py-3' : 'py-2'} cursor-pointer hover:bg-paper-card`}
                      >
                        <div title={it.title || 'Без названия'} className="truncate text-[13px] font-medium text-paper-ink">
                          {it.title || 'Без названия'}
                        </div>
                        <div className="text-[11px] text-paper-ink-3 truncate">
                          {formatMeta(it) && <span>{formatMeta(it)} · </span>}
                          <span>{where}</span>
                        </div>
                        {it.quote && (
                          <div className="mt-[2px] text-[11px] leading-[1.4] text-paper-ink-3 line-clamp-2">{it.quote}</div>
                        )}
                      </div>
                    );
                  }
                  const m = it.match || {};
                  const inTitle = m.field === 'title';
                  return (
                    <div
                      key={it.id}
                      onClick={() => onOpenHistoryItem(it.id, {
                        query: resultsQuery, field: m.field, summaryId: m.summary_id || null,
                      })}
                      className={`rounded-[10px] px-3 ${mobile ? 'py-3' : 'py-2'} cursor-pointer hover:bg-paper-card`}
                    >
                      <div className="flex items-center gap-[6px]">
                        <div
                          title={it.title || 'Без названия'}
                          className="flex-1 min-w-0 truncate text-[13px] font-medium text-paper-ink"
                        >
                          {inTitle
                            ? <Highlighted text={m.snippet || it.title} highlights={m.highlights} />
                            : (it.title || 'Без названия')}
                        </div>
                        {it.shared && (
                          <span title="Доступ по ссылке" className="text-[11px] text-paper-accent shrink-0">🔗</span>
                        )}
                        {it.type === 'dual_channel' && (
                          <span className="shrink-0 rounded-badge bg-paper-bg px-1 text-[9px] font-bold tracking-[0.05em] leading-tight text-paper-ink-2">
                            L/R
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-paper-ink-3 truncate">
                        {formatMeta(it) && <span>{formatMeta(it)} · </span>}
                        {MATCH_FIELD_LABEL[m.field] && <span>{MATCH_FIELD_LABEL[m.field]}</span>}
                        {folderTag(it)}
                      </div>
                      {!inTitle && m.snippet && (
                        <div className="mt-[2px] text-[11px] leading-[1.4] text-paper-ink-3 line-clamp-2">
                          <Highlighted text={m.snippet} highlights={m.highlights} />
                        </div>
                      )}
                    </div>
                  );
  };

  const shownResults = results && hidden.length ? results.filter((it) => !hidden.includes(it.id)) : results;
  const shownTotal = Math.max(0, total - (items.length - shownItems.length));

  const label = accountLabel(email, displayName);
  const initials = accountInitials(label);

  // Полоса остатка рисуется только там, где есть от чего считать долю. У
  // «Оплаты по факту» audio.limit — это подарок, а не потолок: доля от него
  // показывала бы пустую полосу клиенту с деньгами на счету.
  const audio = quota && quota.audio;
  // Сотруднику лимит ничего не запрещает, поэтому и полосы остатка у него нет:
  // заполненная на два процента шкала читается как «всё кончилось».
  const enforced = quotaEnforced(quota);
  const quotaPct = enforced && audio && typeof audio.limit === 'number' && audio.limit > 0
    ? Math.max(0, Math.min(100, Math.round(((audio.limit - (audio.used || 0)) / audio.limit) * 100)))
    : null;
  const alarm = isAlarming(quotaLevelValue);
  // «Пополнить» ведёт в Тарифы, поэтому и показывается ровно там, где этот
  // пункт вообще существует: в издании onprem платёжного контура нет.
  const canTopUp = canOpenPricing(role);

  // 🔴 Карточка остатка — инструмент того, кто платит: клиента, плательщика
  // организации и её участника (пакет общий, и он кончается). Сотруднику её
  // не показываем вовсе: лимит его не останавливает, а «Пополнить» под цифрой
  // расхода — прямое приглашение к покупке, которой не существует. Свой расход
  // он видит на экране «Метрики», где числам и место (замечание владельца
  // 21.09.2026, в тот же день, когда карточка перестала врать про остаток).
  const payg = quota?.balance_rub !== undefined && quota?.balance_rub !== null;
  let quotaValue = '';
  if (payg) {
    quotaValue = `${Math.floor(quota.gift_minutes_left)} мин${quota.balance_rub > 0 ? ` + ${formatRub(quota.balance_rub)}` : ''}`;
  } else if (enforced) {
    quotaValue = quotaText ? quotaText.replace(/^осталось /, '') : '';
  }

  // Остаток — ещё и в мобильную шапку: панель там спрятана в шторку, а цифра,
  // ради которой идут в тарифы, должна быть видна без лишнего нажатия.
  useEffect(() => {
    if (onQuotaChange) onQuotaChange(quotaValue ? { value: quotaValue, alarm, canTopUp } : null);
  }, [onQuotaChange, quotaValue, alarm, canTopUp]);

  // В шторке пункты выше и крупнее: палец, а не курсор (HANDOFF: тапы ≥ 44px).
  const ITEM = `w-full text-left rounded-[10px] px-3 ${mobile ? 'min-h-12 py-3' : 'py-2'} transition-colors`;

  return (
    <aside
      className={`relative shrink-0 ${mobile ? 'w-full pt-6' : 'pt-[22px]'} h-full flex flex-col px-4 pb-4 font-ui`}
      style={mobile ? undefined : { width: shownWidth }}
      data-testid="app-sidebar"
    >
      {/* Лого + марка. Сотрудника ведёт на демо-страницу, клиента — на витрину
          продукта: демо для сотрудников клиенту не предназначено, и на прогоне
          24.09 клиент по логотипу попал именно туда. */}
      <div className={`px-2 ${mobile ? 'pb-3' : 'pb-5'}`}>
        <a
          href={logoHref(role)}
          target="_blank"
          rel="noopener noreferrer"
          title={role === 'client' ? 'О продукте' : 'Демо и материалы для продажи'}
          data-testid="sidebar-logo"
          className="block cursor-pointer hover:opacity-80 transition-opacity"
        >
          {/* В шторке телефона — одной строкой: место сверху там дороже
              (просьба владельца 27.09). */}
          <span className={mobile ? 'flex items-baseline gap-3' : 'block'}>
            <BitLogo />
            <span className={mobile ? '' : 'block mt-[6px]'}><ProductMark /></span>
          </span>
        </a>
      </div>

      {/* Навигация. Без иконок — так в «Бумаге»: строка читается именем, а
          пиктограммы у восьми разделов начинают спорить друг с другом. */}
      <nav className="flex flex-col gap-[2px]">
        {visibleNav({ isAdmin, role, recognitionV2: RECOGNITION_V2, hasOrganization }).map(({ id, label }) => {
          const active = activeTab === id;
          return (
            <button
              key={id}
              onClick={() => onTabChange(id)}
              className={`${ITEM} ${mobile ? 'text-[15px]' : 'text-[14px]'} ${
                active
                  ? 'bg-paper-card font-semibold text-paper-ink'
                  : 'font-medium text-paper-ink-2 hover:bg-paper-panel'
              }`}
            >
              {label}
            </button>
          );
        })}
      </nav>

      {historyEnabled ? (
        // История — на своей подложке, на тон светлее фона страницы: блок
        // отделяется от навигации заливкой, а не рамкой (решение владельца 29.09).
        <div data-testid="history-panel" role="region" aria-label="История" className="mt-[26px] flex-1 min-h-0 flex flex-col rounded-panel bg-paper-panel p-[6px]">
          {/* Поле поиска — оно же заголовок блока: отдельной строки «История» нет,
              место отдано списку (решение владельца 10.10.2026). Блок и так
              выделен подложкой, а чей это поиск, говорит подсказка в поле.
              Поле поиска стоит всегда — отдельной кнопки «Найти» больше нет.
              В шторке телефона шрифт 16px: мельче iOS увеличивает страницу при
              фокусе, и крестик под палец (HANDOFF: тапы ≥ 44px). */}
          <div className="relative mb-[6px]">
            <Search className="pointer-events-none absolute left-[10px] top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-paper-hint" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              maxLength={200}
              aria-label="Поиск по записям"
              placeholder="Поиск в Истории"
              enterKeyHint="search"
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && query) { e.preventDefault(); clearSearch(); }
              }}
              className={`w-full rounded-[10px] bg-paper-card pl-8 pr-8 ${mobile ? 'py-2 text-[16px]' : 'py-[6px] text-[13px]'} text-paper-ink outline-none focus-visible:ring-1 focus-visible:ring-paper-hint placeholder:text-paper-hint`}
            />
            {query && (
              <button
                type="button"
                onClick={() => { clearSearch(); if (searchRef.current) searchRef.current.focus(); }}
                title="Очистить поиск"
                aria-label="Очистить поиск"
                className={`absolute right-0 top-1/2 -translate-y-1/2 flex items-center justify-center ${mobile ? 'w-11 h-11' : 'w-8 h-8'} text-paper-hint hover:text-paper-ink`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-[2px]">
            {/* Повторный поиск после пустой выдачи или отказа: на экране ничего,
                кроме прежнего сообщения, — без «Ищем…» казалось, что поле не слышит. */}
            {(results === null || results.length === 0) && searchState === 'loading' && (
              <div className="px-3 py-2 text-[12px] text-paper-ink-3">Ищем…</div>
            )}
            {results !== null ? (
              <>
                {searchState === 'unavailable' && (
                  <div className="px-3 py-2 text-[12px] leading-[1.5] text-paper-ink-3">Поиск временно недоступен</div>
                )}
                {searchState === 'error' && (
                  <div className="px-3 py-2 text-[13px] text-paper-accent">Не удалось выполнить поиск</div>
                )}
                {searchState === 'done' && shownResults.length === 0 && (
                  <div className="px-3 py-2 text-[12px] leading-[1.5] text-paper-ink-3">
                    <div className="font-medium text-paper-ink-2">Ничего не нашлось.</div>
                    <div>Поиск понимает смысл и слова в любой форме — попробуйте сказать иначе; точную фразу возьмите в кавычки.</div>
                  </div>
                )}
                {/* Переименования и удаления в выдаче нет: это поиск, а порядок
                    в записях наводится в обычном списке. */}
                {shownResults.map((it, idx) => {
                  // Подписи групп — чтобы было видно, почему запись нашлась: сверху
                  // похожее по смыслу, ниже совпадения слов (переключателя нет).
                  const prev = shownResults[idx - 1];
                  const caption = (idx === 0 && it.sense && 'Похоже по смыслу')
                    || (!it.sense && (prev?.sense) && 'Совпадения слов') || null;
                  const row = renderResult(it);
                  return caption ? (
                    <React.Fragment key={it.id}>
                      <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-[0.08em] text-paper-hint">{caption}</div>
                      {row}
                    </React.Fragment>
                  ) : row;
                })}
                {results.length < resultsTotal && (
                  <button
                    onClick={loadMoreResults}
                    disabled={loadingMore}
                    className="w-full rounded-[10px] py-2 text-[12px] text-paper-ink-3 hover:bg-paper-card disabled:text-paper-hint"
                  >
                    {loadingMore ? 'Загружаем…' : `Показать ещё (${resultsTotal - results.length})`}
                  </button>
                )}
              </>
            ) : searchState !== 'loading' && (
              <>
                {loadError && <div className="px-3 py-2 text-[13px] text-paper-accent">Не удалось загрузить</div>}
                {folderError && <div role="alert" className="px-3 py-2 text-[13px] text-paper-accent">{folderError}</div>}
                {folders !== null && openFolderId === null && (
                  <HistoryFolders
                    folders={folders}
                    activeId={null}
                    mobile={mobile}
                    onOpen={openFolder}
                    onCreate={createFolder}
                    onRename={renameFolder}
                    onDelete={deleteFolder}
                    hint={foldersHint}
                    onHintAction={onFoldersHintAction}
                  />
                )}
                {openFolderId !== null && (
                  <div className="px-3 pb-1">
                    <button
                      type="button"
                      onClick={() => openFolder(null)}
                      className="text-[12px] text-paper-ink-3 hover:text-paper-ink"
                    >
                      ← Все записи
                    </button>
                    <div title={folderName(openFolderId) || ''} className="truncate text-[13px] font-semibold text-paper-ink">
                      {folderName(openFolderId) || 'Папка'}
                    </div>
                  </div>
                )}
                {!loadError && !loading && shownItems.length === 0 && (
                  <div className="px-3 py-2 text-[12px] leading-[1.5] text-paper-ink-3">
                    {openFolderId !== null
                      ? 'В папке пока пусто. Переложите запись кнопкой «В папку».'
                      : 'Здесь появятся ваши записи. Первая — как только распознаете.'}
                  </div>
                )}
                {shownItems.map((it) => (
                  <div
                    key={it.id}
                    onClick={() => onOpenHistoryItem(it.id)}
                    // Подложка Истории — paper-panel, поэтому наведение на тон
                    // светлее, белым. Строку в правке не подсвечиваем: белое поле
                    // названия слилось бы с ней.
                    className={`group rounded-[10px] px-3 py-2 cursor-pointer ${editingId === it.id ? '' : 'hover:bg-paper-card'}`}
                  >
                    <div className="flex items-center gap-[6px]">
                      {editingId === it.id ? (
                        <input
                          autoFocus
                          value={editValue}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={() => commitRename(it)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); commitRename(it); }
                            else if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                          }}
                          className="flex-1 min-w-0 rounded-[6px] bg-paper-card px-[6px] py-[2px] text-[13px] font-medium text-paper-ink outline-none"
                        />
                      ) : (
                        <div
                          title={it.title || 'Без названия'}
                          className="flex-1 min-w-0 truncate text-[13px] font-medium text-paper-ink"
                        >
                          {it.title || 'Без названия'}
                        </div>
                      )}
                      {it.shared && editingId !== it.id && (
                        <span title="Доступ по ссылке" className="text-[11px] text-paper-accent shrink-0">🔗</span>
                      )}
                      {it.type === 'dual_channel' && editingId !== it.id && (
                        <span className="shrink-0 rounded-badge bg-paper-bg px-1 text-[9px] font-bold tracking-[0.05em] leading-tight text-paper-ink-2">
                          L/R
                        </span>
                      )}
                      {/* Переименование и удаление макет не рисует, а без них записи
                          не привести в порядок. Показываем по наведению, серым; на
                          телефоне наведения нет — там видны всегда и крупнее. */}
                      {editingId !== it.id && (
                        <>
                          {folders !== null && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setMoveMenuFor((cur) => (cur === it.id ? null : it.id)); }}
                              // Закрытие меню по mousedown вне него не должно «съесть» клик по
                              // кнопке и тут же открыть меню заново: клик сам переключает.
                              onMouseDown={(e) => e.stopPropagation()}
                              title="В папку"
                              aria-label="В папку"
                              className={`${mobile ? 'w-9 h-9 -my-2 flex items-center justify-center' : 'opacity-0 group-hover:opacity-100 p-[2px]'} shrink-0 text-paper-hint hover:text-paper-ink`}
                            >
                              <FolderInput className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <button
                            onClick={(e) => startRename(e, it)}
                            title="Переименовать"
                            aria-label="Переименовать"
                            className={`${mobile ? 'w-9 h-9 -my-2 flex items-center justify-center' : 'opacity-0 group-hover:opacity-100 p-[2px]'} shrink-0 text-paper-hint hover:text-paper-ink`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={(e) => handleDelete(e, it.id, it.title)}
                            title="Удалить"
                            aria-label="Удалить"
                            // Корзина видна всегда, приглушённо: невидимую до наведения не
                            // находили, а с планшета её не было вовсе (обращение ac3cd227).
                            className={`${mobile ? 'w-9 h-9 -my-2 flex items-center justify-center' : 'opacity-40 group-hover:opacity-100 focus:opacity-100 p-[2px]'} shrink-0 text-paper-hint hover:text-paper-accent`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                    <div className="text-[11px] text-paper-ink-3 truncate">
                      {formatMeta(it) || it.snippet}
                      {folderTag(it)}
                    </div>
                    {folders !== null && moveMenuFor === it.id && (
                      <div className="mt-1">
                        <MoveToFolderMenu
                          folders={folders}
                          currentId={it.folder_id || null}
                          onMove={(fid) => moveItem(it, fid)}
                          onCreateAndMove={(name) => createFolderAndMove(it, name)}
                          onClose={closeMoveMenu}
                        />
                      </div>
                    )}
                  </div>
                ))}
                {shownItems.length < shownTotal && (
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="w-full rounded-[10px] py-2 text-[12px] text-paper-ink-3 hover:bg-paper-card disabled:text-paper-hint"
                  >
                    {loadingMore ? 'Загружаем…' : `Показать ещё (${shownTotal - shownItems.length})`}
                  </button>
                )}
              </>
            )}
          </div>
          {/* Окно отмены удаления: запись уже скрыта, на сервере она ещё есть. */}
          {undo && (
            <div
              data-testid="undo-delete"
              role="status"
              className="mt-[6px] flex items-center justify-between gap-2 rounded-[10px] bg-paper-card px-3 py-2 text-[12px] text-paper-ink-2"
            >
              <span>Запись удалена</span>
              <button
                type="button"
                onClick={undoDelete}
                className={`${mobile ? 'min-h-9 px-2' : ''} font-semibold text-paper-accent hover:underline`}
              >
                Отменить
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 min-h-0 pt-4">
          <div className="rounded-panel bg-paper-card p-4">
            <div className="text-[13px] font-semibold text-paper-ink">Сохраняйте свои расшифровки</div>
            <div className="mt-1 text-[12px] leading-[1.5] text-paper-ink-2">
              Возвращайтесь к прошлым встречам и протоколам когда нужно. Видно только вам —
              срок хранения и удаление под вашим контролем.
            </div>
            <button
              onClick={onEnableHistory}
              className="mt-3 w-full rounded-[10px] bg-paper-accent py-2 text-[13px] font-semibold text-white hover:bg-paper-accent-hover transition-colors"
            >
              Включить Историю
            </button>
          </div>
        </div>
      )}

      {/* Остаток — отдельной карточкой, как в «Бумаге». Раньше он жил строкой
          под почтой в кнопке меню и терялся: цифра, ради которой человек идёт
          в тарифы, была самой мелкой на панели. */}
      {quotaValue && (
        <button
          type="button"
          onClick={() => canTopUp && onTabChange('pricing')}
          className={`mt-3 w-full rounded-control bg-paper-card px-[14px] py-3 text-left
                      ${canTopUp ? 'hover:bg-paper-panel transition-colors' : 'cursor-default'}`}
        >
          <div className="flex items-baseline justify-between text-[12px]">
            <span className="font-semibold text-paper-ink">Остаток</span>
            <span data-testid="quota-balance"
                  className={`font-mono ${alarm ? 'text-paper-accent' : 'text-paper-ink-2'}`}>
              {quotaValue}
            </span>
          </div>
          {quotaPct !== null && (
            <div className="mt-2 h-1 rounded-[2px] bg-paper-bg overflow-hidden">
              <div className={`h-full ${alarm ? 'bg-paper-accent' : 'bg-paper-ink'}`}
                   style={{ width: `${quotaPct}%` }} />
            </div>
          )}
          {canTopUp && (
            <div className="mt-2 text-[11px] text-paper-ink-3">
              <span className="font-medium text-paper-ink">Пополнить</span>
            </div>
          )}
        </button>
      )}

      {/* Юзер */}
      <div ref={menuRef} className="relative">
        {menuOpen && (
          <div className="absolute bottom-full left-0 right-0 mb-[6px] flex flex-col rounded-control bg-paper-card p-[6px] shadow-[0_8px_24px_rgba(0,0,0,.08)]">
            <button onClick={() => { setMenuOpen(false); onOpenProfile(); }}
                    className="rounded-chip px-[10px] py-2 text-left text-[13px] text-paper-ink hover:bg-paper-panel">
              Личный кабинет
            </button>
            <button onClick={() => { setMenuOpen(false); onTabChange('metrics'); }}
                    className="rounded-chip px-[10px] py-2 text-left text-[13px] text-paper-ink hover:bg-paper-panel">
              Метрики
            </button>
            {/* Админка — инструмент для большого экрана, на телефоне пункта нет
                (решение владельца 27.09): таблицы и фильтры там не помещаются. */}
            {isAdmin && !mobile && (
              <button onClick={() => { setMenuOpen(false); onOpenAdmin(); }}
                      className="rounded-chip px-[10px] py-2 text-left text-[13px] text-paper-ink hover:bg-paper-panel">
                Администрирование
              </button>
            )}
            {/* Демо клиенту пока выдаёт только админ (решение владельца 05.10). */}
            {onOpenDemo && isAdmin && (
              <button onClick={() => { setMenuOpen(false); onOpenDemo(); }}
                      className="rounded-chip px-[10px] py-2 text-left text-[13px] text-paper-ink hover:bg-paper-panel">
                Выдать демо клиенту
              </button>
            )}
            <button onClick={() => { setMenuOpen(false); onOpenFeedback?.(); }}
                    className="flex items-center rounded-chip px-[10px] py-2 text-left text-[13px] text-paper-ink hover:bg-paper-panel">
              {FEEDBACK_ENTRY_LABEL}
              {unreadFeedback > 0 && (
                <span className="ml-auto rounded-badge bg-paper-accent px-[6px] py-[1px] text-[11px] font-semibold text-white"
                      title="Есть непрочитанные решения">
                  {unreadFeedback}
                </span>
              )}
            </button>
            <button onClick={() => { setMenuOpen(false); onLogout(); }}
                    className="rounded-chip px-[10px] py-2 text-left text-[13px] text-paper-ink-2 hover:bg-paper-panel">
              Выйти
            </button>
          </div>
        )}
        {/* Отдельной карточкой, как остаток над ним: строкой на фоне панели
            блок пользователя не замечали (замечание владельца 24.09.2026). */}
        <button onClick={() => setMenuOpen((o) => !o)}
                aria-expanded={menuOpen}
                data-testid="user-menu-toggle"
                title={label.isName ? `${label.text} · ${email}` : undefined}
                className={`mt-3 w-full flex items-center gap-[10px] rounded-control px-3 py-[10px] text-left transition-colors
                            ${menuOpen ? 'bg-paper-panel' : 'bg-paper-card hover:bg-paper-panel'}`}>
          <span className="relative w-7 h-7 shrink-0 rounded-chip bg-paper-ink text-white flex items-center justify-center text-[11px] font-semibold">
            {initials}
            {/* Меню свёрнуто по умолчанию, поэтому метка нужна и снаружи: иначе
                непрочитанное решение не видно вообще ниоткуда. */}
            {unreadFeedback > 0 && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-paper-accent shadow-[0_0_0_2px_#EDECE8]"
                    aria-label="Есть непрочитанные решения" />
            )}
          </span>
          <span className="flex-1 min-w-0 truncate text-[12px] font-medium text-paper-ink">
            {label.text}
          </span>
          <ChevronUp className={`h-4 w-4 shrink-0 text-paper-hint transition-transform ${menuOpen ? '' : 'rotate-180'}`} />
        </button>
      </div>
      {/* Ручка ширины — полоса у правого края. В покое не видна (в «Бумаге» нет
          рамок), по наведению и при перетаскивании — тонкая заливка. Двойной
          щелчок возвращает ширину по умолчанию. В шторке телефона её нет.
          Последней в разметке: позиция абсолютная, а в порядке Tab первой
          остановкой всего приложения она была бы некстати. */}
      {!mobile && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Ширина панели"
          aria-valuemin={SIDEBAR_WIDTH_MIN}
          aria-valuemax={maxWidth}
          aria-valuenow={shownWidth}
          tabIndex={0}
          data-testid="sidebar-resize"
          onPointerDown={onResizeDown}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeEnd}
          onPointerCancel={onResizeEnd}
          onLostPointerCapture={onResizeEnd}
          onDoubleClick={() => commitWidth(SIDEBAR_WIDTH_DEFAULT)}
          onKeyDown={onResizeKey}
          className="group absolute inset-y-0 -right-[3px] z-10 w-[6px] flex justify-center cursor-col-resize outline-none touch-none"
        >
          <span className={`h-full w-[2px] rounded-[1px] transition-colors ${
            dragging ? 'bg-paper-hint' : 'bg-transparent group-hover:bg-paper-track group-focus-visible:bg-paper-hint'
          }`} />
        </div>
      )}
    </aside>
  );
}
